import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Inject,
  Output
} from '@angular/core';
import {
  AviaSearchAppliedFilter,
  AviaSearchAppliedFilterGroup,
  AviaSearchFilterItem,
  AviaSearchFilters
} from '../../../services/router/interfaces/avia-search';
import { APP_CONFIG, AppConfig } from '../../../config/app.config';
import { WindowRefService } from '../../../services/window-ref.service';
import { FilterPillComponent } from './filter-pill/filter-pill.component';
import { FilterSheetComponent } from './filter-sheet/filter-sheet.component';

@Component({
  selector: 'app-filter-pills',
  templateUrl: './filter-pills.component.html',
  styleUrls: ['./filter-pills.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FilterPillComponent,
    FilterSheetComponent,
  ],
  standalone: true
})
export class FilterPillsComponent {
  private readonly stopoverFilterKeys = [
    'stops',
    'stopoverDuration',
    'withoutNightStopovers',
  ];
  private readonly departureTimeFilterKeys = [
    'departureTimeFromOrigin',
    'departureTimeFromDestination',
  ];

  @Input() filters: AviaSearchFilters = [];
  @Input() appliedFilters: AviaSearchAppliedFilter[] = [];
  @Output() applyFilters = new EventEmitter<AviaSearchAppliedFilter>();
  @Output() applyFilterGroup = new EventEmitter<AviaSearchAppliedFilterGroup>();
  @Output() resetFilter = new EventEmitter<string>();
  @Output() resetFilterGroup = new EventEmitter<string[]>();

  public activeFilter: AviaSearchFilterItem | null = null;
  public activeFilterGroup: string | null = null;

  constructor(
    @Inject(APP_CONFIG) private config: AppConfig,
    private windowService: WindowRefService,
    private cdRef: ChangeDetectorRef,
  ) {
  }

  get visibleFilters(): AviaSearchFilters {
    const firstStopoverFilter = this.filters.find((filter) => this.isStopoverFilter(filter.filterKey));

    const filters = this.filters.flatMap((filter) => {
      if (['singleAirline', 'excludeLcc'].includes(filter.filterKey)) { return []; }
      if (!this.isStopoverFilter(filter.filterKey)) {
        return [filter];
      }

      return filter === firstStopoverFilter
        ? [{ filterKey: 'stopovers', type: 'group', name: this.config.translates.aviaListing.stopovers }]
        : [];
    });

    return filters;
  }

  get stopoversLabel(): string {
    return this.stopoverFilterKeys.flatMap((filterKey) => {
      const applied = this.getAppliedFilter(filterKey);
      const filter = this.filters.find((item) => item.filterKey === filterKey);
      if (!applied || !filter) {
        return [];
      }

      if (filterKey === 'withoutNightStopovers') {
        return applied.selected === 'true' ? [filter.name] : [];
      }

      const option = (filter.values || []).find((value) => typeof value === 'string'
        ? value === applied.selected
        : value.filterValue === applied.selected);
      if (typeof option === 'string') {
        return [option];
      }

      return option && !option.isNoop ? [option.viewValue || option.filterValue] : [];
    }).join(' / ');
  }

  showFilters() {
    this.openFilterSheet(null, 'allFilters');
  }

  onOpenSheet(filterType: string) {
    const filter = this.filters.find((item) => item.filterKey === filterType) || null;
    const filterGroup = this.resolveFilterGroup(filterType);
    this.openFilterSheet(filter, filterGroup);
  }

  getAppliedFilter(filterKey: string): AviaSearchAppliedFilter | null {
    if (filterKey === 'airlines') {
      return this.appliedFilters.find((filter) => filter.filterKey === 'airlines')
        || this.appliedFilters.find((filter) => filter.filterKey === 'singleAirline')
        || this.appliedFilters.find((filter) => filter.filterKey === 'excludeLcc')
        || null;
    }
    return this.appliedFilters.find((filter) => filterKey === 'stopovers'
      ? this.isStopoverFilter(filter.filterKey)
      : filter.filterKey === filterKey) || null;
  }

  onApplyFilter(filter: AviaSearchAppliedFilter) {
    this.applyFilters.emit(filter);
  }

  onApplyFilterGroup(filterGroup: AviaSearchAppliedFilterGroup) {
    this.applyFilterGroup.emit(filterGroup);
  }

  onResetFilter(filterKey: string) {
    if (filterKey === 'airlines') {
      this.resetFilterGroup.emit(['airlines', 'singleAirline', 'excludeLcc']);
      return;
    }
    if (filterKey === 'stopovers') {
      this.resetFilterGroup.emit(this.stopoverFilterKeys);
      return;
    }

    this.resetFilter.emit(filterKey);
  }

  onResetFilterGroup(filterKeys: string[]) {
    this.resetFilterGroup.emit(filterKeys);
  }

  private openFilterSheet(filter: AviaSearchFilterItem | null, filterGroup: string | null = null) {
    const isAllFilters = filterGroup === 'allFilters';

    this.activeFilter = filter;
    this.activeFilterGroup = filterGroup;
    this.cdRef.detectChanges();

    const modal = this.windowService.nativeWindow.modal;
    if (!modal?.open) {
      return;
    }

    modal.open({
      id: 'ng-modal-avia-filter-sheet',
      type: isAllFilters ? (modal.types?.FULL || 'full') : (modal.types?.BOTTOM || 'bottom'),
      parentCssClass: isAllFilters ? 'filters-modal filters-modal_full' : 'filters-modal',
      header: false,
      footer: false,
      content: {
        htmlDetachSelectorId: 'ng-modal-avia-filter-sheet-content',
      },
      onBeforeClose: () => {
        this.activeFilter = null;
        this.activeFilterGroup = null;
        this.cdRef.detectChanges();
      },
    });
  }

  private isStopoverFilter(filterType: string): boolean {
    return this.stopoverFilterKeys.includes(filterType);
  }

  private resolveFilterGroup(filterType: string): string | null {
    if (filterType === 'stopovers' || this.isStopoverFilter(filterType)) {
      return 'stopovers';
    }

    if (this.departureTimeFilterKeys.includes(filterType)) {
      return 'departureTime';
    }

    return null;
  }
}
